import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
// import { demoSingleCaseHistory } from "../../../../utils/demoData";
import DisplayCaseInformation from "../../PersonView/DisplaySchema";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* ----------------------------------
   Sortable Card Wrapper
----------------------------------- */
const SortableCard = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: "grab",
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </Box>
  );
};

/* ----------------------------------
   Main Component
----------------------------------- */
const CaseHistoryCard = ({ info }) => {
  const [items, setItems] = useState([]);
  const [records, setRecords] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [recordsCache, setRecordsCache] = useState({});
  const [isDraggingAny, setIsDraggingAny] = useState(false);
  /* Initialize sortable items */
  useEffect(() => {
    const filtered =
      info?.filter((item) => item.ID?.VALUE && item.SOURCE?.VALUE) || [];

    setItems(
      filtered.map((item, index) => ({
        ...item,
        _dragId: `${item.ID.VALUE}-${index}`,
      })),
    );
  }, [info]);

  const formatKey = (key) =>
    key
      .replace(/_/g, " ")
      .replace(
        /\w\S*/g,
        (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
      );

  const fetchRecords = (item) => {
    if (!item.ID?.VALUE)
      return toast.error("Something is missing, ID or SOURCE!");

    if (recordsCache[item.ID.VALUE]) {
      setRecords(recordsCache[item.ID.VALUE]);
      return;
    }

    setIsLoading(true);
    fetch(
      `/api/getcase?id=${item.ID.VALUE}&source=${item.SOURCE.VALUE}&preview=yes`,
    )
      .then((res) => res.json())
      .then((data) => {
        setRecords(data);
        setRecordsCache((prev) => ({
          ...prev,
          [item.ID.VALUE]: data,
        }));
      })
      .finally(() => setIsLoading(false));
  };

  /* Handle drag reorder */
  const handleDragEnd = (event) => {
    setIsDraggingAny(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i._dragId === active.id);
      const newIndex = prev.findIndex((i) => i._dragId === over.id);

      const updated = [...prev];
      const [moved] = updated.splice(oldIndex, 1);
      updated.splice(newIndex, 0, moved);
      return updated;
    });
  };

  const handleDragStart = () => {
    setIsDraggingAny(true);
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext
        items={items.map((i) => i._dragId)}
        strategy={verticalListSortingStrategy}
      >
        <Box>
          {items.map((item) => (
            <SortableCard key={item._dragId} id={item._dragId}>
              <Popover
                placement="auto"
                trigger="hover"
                closeOnBlur={false}
                isOpen={isDraggingAny ? false : undefined}
              >
                <PopoverTrigger>
                  <Box
                    p={4}
                    bg="gray.100"
                    boxShadow="md"
                    borderRadius="md"
                    mt={4}
                    maxW="350px"
                    onMouseEnter={() => fetchRecords(item)}
                  >
                    {Object.entries(item)
                      .filter(([key]) => !["_dragId", "id"].includes(key))
                      .map(([key, value]) => (
                        <Flex key={key} fontSize="sm" justify="space-between">
                          <Box w="40%">{formatKey(key)}</Box>
                          <Box>:</Box>
                          <Box w="60%">
                            <Text>{value?.VALUE}</Text>
                          </Box>
                        </Flex>
                      ))}
                  </Box>
                </PopoverTrigger>

                <Portal>
                  <PopoverContent w="60vw" maxH="640px" overflowY="auto">
                    <PopoverArrow />
                    <PopoverBody>
                      <DisplayCaseInformation
                        data={records}
                        isLoading={isLoading}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </SortableCard>
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default CaseHistoryCard;
